import { FC, Fragment, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { Box, Button, Typography, AutocompleteChangeReason, Card, Grid } from '@mui/material';
// components
import AddBlendModal from './AddBlendModal';
import { Alert } from 'components/common/Alert';
import IngredientsCard from './IngredientsCard';
import IngredientBlends from './IngredientBlends';
import NoIngredientFound from './NoIngredientFound';
import AddIngredientModal from './AddIngredientModal';
import CategorySelector from 'components/common/simpleSelectors/Category';
import IngredientSelector from 'components/common/selectors/ingredients';
import IngredientTypeSelector from 'components/common/selectors/ingredientType';
// interfaces, constants, styles, context
import {
  VIEW_TEXT,
  SEARCH_TEXT,
  ADD_BLEND_TEXT,
  FACT_SHEET_TEXT,
  INGREDIENTS_TEXT,
  PRIMARY_CATEGORY_TEXT,
  SECONDARY_CATEGORY_TEXT,
  INGREDIENT_ALREADY_EXIST_TEX,
  ADD_INGREDIENT_TEXT,
  INGREDIENT_TYPE_TEXT,
  selectTypeInitialValues,
} from 'constants/index';
import { ProductTypes } from 'generated/graphql';
import { displayFlexAlignCenter } from 'theme/styleConstant';
import { flexCenterBetween, forInputLabelBg } from 'styles/commonComponentStyle';
import { AddNewFormulaFormType, IngredientItemType, IngredientsListProps, SelectType } from 'interfaces';

const IngredientsList: FC<IngredientsListProps> = ({
  xl,
  onBlendAdd,
  ingredientsBlend,
  isFactBox = false,
  formulaIngredients,
  isShowCategory = false,
  productFormulationType,
  showAddBlendBtn = true,
  productType,
  handleFactSheet,
  onUnblend,
}) => {
  const [primary, setPrimary] = useState<SelectType>(selectTypeInitialValues);
  const [secondary, setSecondary] = useState<SelectType>(selectTypeInitialValues);
  const [open, setOpen] = useState<boolean>(false);
  const [openAddIngredient, setOpenAddIngredient] = useState<boolean>(false);
  const [type, setIngredientType] = useState<SelectType[]>([]);

  const { value: primaryId } = primary || {};
  const { value: secondaryId } = secondary || {};

  const { control, trigger } = useFormContext<AddNewFormulaFormType>();

  const { fields, remove, prepend, move } = useFieldArray({
    name: 'formulaIngredients',
    control,
  });

  const onRemove = (index: number) => {
    remove(index);
  };

  const onChange = (value: IngredientItemType, reason: AutocompleteChangeReason) => {
    if (reason === 'selectOption') {
      const isFound = fields?.some(({ ingredientId }) => ingredientId === value?.ingredientId);
      if (isFound) {
        Alert.warning(INGREDIENT_ALREADY_EXIST_TEX);
      } else {
        prepend({
          ...value,
          overage: productType === ProductTypes.Gummies ? value.gummyOverage : value.overage,
        });
      }
    }
  };

  const handleBlendClick = async () => {
    const isValid = await trigger('formulaIngredients');
    if (isValid) {
      setOpen(!open);
    }
  };

  const handleUnblend = (blendName: string, blendIngredients: IngredientItemType[]) => {
    // Add the blend ingredients back to the form
    blendIngredients?.forEach((ingredient) => {
      // Check if ingredient already exists to avoid duplicates
      const isFound = fields?.some(({ ingredientId }) => ingredientId === ingredient?.ingredientId);
      if (!isFound) {
        prepend({ ...ingredient });
      }
    });

    // Call the parent onUnblend handler if provided
    if (onUnblend) {
      onUnblend(blendName, blendIngredients);
    }
  };

  const noData = !(formulaIngredients?.length || ingredientsBlend.length);
  const showBlendBtn = formulaIngredients?.length && showAddBlendBtn;

  return (
    <Card sx={forInputLabelBg}>
      <Box mb={3} sx={flexCenterBetween}>
        <Typography variant="h6">{INGREDIENTS_TEXT}</Typography>
        <Box sx={{ ...displayFlexAlignCenter, gap: 2 }}>
          <Button variant={'outlined'} onClick={() => setOpenAddIngredient(true)}>
            {ADD_INGREDIENT_TEXT}
          </Button>
          {showAddBlendBtn ? (
            <Box sx={{ ...displayFlexAlignCenter, gap: 2 }}>
              {showBlendBtn ? (
                <Button variant="outlined" onClick={handleBlendClick}>
                  {ADD_BLEND_TEXT}
                </Button>
              ) : (
                <Fragment />
              )}

              <Button variant="contained" onClick={handleFactSheet} disabled={noData}>
                {VIEW_TEXT} {FACT_SHEET_TEXT}
              </Button>
            </Box>
          ) : (
            <></>
          )}
        </Box>
      </Box>
      {isShowCategory ? (
        <Grid container spacing={2} mb={2}>
          <Grid item xs={12} lg={6}>
            <CategorySelector
              key="primary"
              name="primary"
              value={primary}
              isClearable={false}
              title={PRIMARY_CATEGORY_TEXT}
              handleChange={(value) => setPrimary(value)}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <CategorySelector
              key="secondary"
              primary={false}
              value={secondary}
              name="secondary"
              isClearable={false}
              title={SECONDARY_CATEGORY_TEXT}
              handleChange={(value) => setSecondary(value)}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <IngredientTypeSelector
              key="type"
              name="type"
              value={type}
              isClearable={false}
              title={INGREDIENT_TYPE_TEXT}
              handleChange={(value: SelectType[]) => setIngredientType(value)}
            />
          </Grid>
        </Grid>
      ) : (
        <Fragment />
      )}
      <IngredientSelector
        isMultiple
        name="ingredients"
        title={SEARCH_TEXT}
        primaryId={primaryId}
        secondaryId={secondaryId}
        type={type}
        handleChange={onChange}
      />

      <Box pb={2} mt={2} pr={1} maxHeight="calc(100vh - 380px)" overflow={'auto'}>
        <IngredientBlends
          ingredientsBlend={ingredientsBlend}
          onBlendAdd={onBlendAdd}
          onUnblend={handleUnblend}
        />

        <IngredientsCard
          xl={xl}
          fields={fields}
          onRemove={onRemove}
          isFactBox={isFactBox}
          productType={productType}
          productFormulationType={productFormulationType}
          move={move}
        />
      </Box>

      <NoIngredientFound noData={noData} />

      <AddBlendModal
        open={open}
        onBlendAdd={onBlendAdd}
        onClose={() => setOpen(!open)}
        ingredientsBlend={ingredientsBlend}
      />

      <AddIngredientModal open={openAddIngredient} onClose={() => setOpenAddIngredient(!openAddIngredient)} />
    </Card>
  );
};

export default IngredientsList;
